import SearchIcon from "@mui/icons-material/Search";
import {
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  InputBaseComponentProps,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { default as PolyfilledSet } from "core-js/actual/set";
import * as React from "react";

import { RegexIcon } from "@/shared/components/icons/RegexIcon";
import { useDebouncedCallback, usePopupState } from "@/shared/hooks";
import { type LogPanelState } from "@/shared/state/visualization";

import {
  ClearFiltersAction,
  DeleteFilterAction,
  SetFilterAction,
  type ColumnFiltersAction,
} from "../columnFilters/actions";
import { ColumnFilterPopover } from "../columnFilters/ColumnFilterPopover";
import { ColumnFiltersState } from "../columnFilters/state";
import { IndexState } from "../IndexState";

import styles from "./LogSearchInput.module.css";
import { SearchMode, isQueryValid } from "./search";

interface LogSearchInputProps {
  hidden: boolean;
  onSearch: (
    query: string,
    columnFilters: Record<string, string>,
    mode: SearchMode,
    signal?: AbortSignal,
  ) => void;
  columnFilters: ColumnFiltersState;
  columnFiltersDispatch: React.Dispatch<ColumnFiltersAction>;
  panelData: LogPanelState["data"];
  searchIndexState: IndexState;
  initialSearchTerm?: string;
  initialSearchMode?: SearchMode;
}

function getPlaceholder(searchMode: SearchMode, searchIndexState: IndexState) {
  if (searchIndexState === IndexState.Empty) {
    return "Search index not yet built";
  }

  if (searchIndexState === IndexState.Building) {
    return "Building search index...";
  }

  return searchMode === SearchMode.Regex
    ? "Search by pattern"
    : "Search by keyword";
}

export function LogSearchInput({
  hidden,
  onSearch,
  panelData,
  searchIndexState,
  columnFilters,
  columnFiltersDispatch,
  initialSearchTerm,
  initialSearchMode,
}: LogSearchInputProps) {
  const [searchTerm, setSearchTerm] = React.useState<string>(
    initialSearchTerm ?? "",
  );
  const [searchMode, setSearchMode] = React.useState<SearchMode>(
    initialSearchMode ?? SearchMode.String,
  );
  const [isValidationError, setIsValidationError] =
    React.useState<boolean>(false);

  // Sync local state when initial props change (e.g., when switching layouts)
  React.useEffect(
    function syncSearchTermFromProps() {
      setSearchTerm(initialSearchTerm ?? "");
    },
    [initialSearchTerm],
  );

  React.useEffect(
    function syncSearchModeFromProps() {
      setSearchMode(initialSearchMode ?? SearchMode.String);
    },
    [initialSearchMode],
  );

  // Debounce time chosen through trial and error
  const debouncedSearch = useDebouncedCallback(onSearch, 300);

  // Clear search state when search is hidden
  React.useEffect(
    function clearSearchOnHide() {
      if (hidden) {
        setSearchTerm("");
        columnFiltersDispatch(new ClearFiltersAction());
      }
    },
    [hidden, columnFiltersDispatch],
  );

  // Only have one active search at a time.
  const abortRef = React.useRef<AbortController>(new AbortController());
  React.useEffect(
    function onQueryChange() {
      const trimmed = searchTerm.trim();
      if (isQueryValid(trimmed, searchMode)) {
        abortRef.current.abort();
        const abort = new AbortController();
        debouncedSearch(
          trimmed,
          columnFilters.filters,
          searchMode,
          abort.signal,
        );
        abortRef.current = abort;
        setIsValidationError(false);
      } else {
        setIsValidationError(true);
      }
    },
    [debouncedSearch, columnFilters.filters, searchTerm, searchMode],
  );

  /**
   * If visibile message paths are removed and the data is filtered, clear the search query and re-trigger search.
   *
   * This needs to happen because we don't know, for any given row,
   * whether that row matched the search query because of the now-removed message path.
   *
   * Once (if) we can select to which message path to apply a filter, this logic can be improved to be more targeted.
   */
  const prevState = React.useRef<LogPanelState["data"]>(panelData);
  React.useEffect(
    function clearQueryOnDataChange() {
      const query = searchTerm.trim();
      if (query.length === 0 || prevState.current === panelData) {
        return;
      }

      const prevMessagePaths = new PolyfilledSet(
        prevState.current.map((topicData) => topicData.messagePath.id),
      );
      const nextMessagePaths = new PolyfilledSet(
        panelData.map((topicData) => topicData.messagePath.id),
      );
      const messagePathsRemoved =
        prevMessagePaths.difference(nextMessagePaths).size > 0;

      const abortController = new AbortController();
      if (messagePathsRemoved) {
        setSearchTerm("");
      }

      prevState.current = panelData;

      return function abort() {
        abortController.abort();
      };
    },
    [panelData, searchTerm, setSearchTerm],
  );

  const activeColumnFilterKeys = Object.keys(columnFilters.filters);
  const lastChipKey = activeColumnFilterKeys.at(-1);

  const deleteLastChip = () => {
    if (lastChipKey !== undefined) {
      columnFiltersDispatch(new DeleteFilterAction({ columnId: lastChipKey }));
    }
  };

  const hasFocus = React.useRef(false);

  return (
    <TextField
      value={searchTerm}
      disabled={searchIndexState !== IndexState.Built}
      fullWidth
      error={isValidationError}
      size="small"
      sx={{
        display: hidden ? "none" : undefined,
      }}
      onFocus={() => (hasFocus.current = true)}
      onBlur={() => (hasFocus.current = false)}
      onKeyDown={(event) => {
        // If the search input is empty and this field is still focused for editing,
        // delete the last applied column filter when the user tries to delete something.
        if (
          event.key === "Backspace" &&
          searchTerm === "" &&
          hasFocus.current
        ) {
          deleteLastChip();
        }
      }}
      placeholder={getPlaceholder(searchMode, searchIndexState)}
      onChange={(event) => setSearchTerm(event.target.value)}
      InputProps={{
        sx: { alignItems: "center" },
        inputProps: {
          className: styles.input,
          hostProps: {
            searchTerm,
            lastChipKey,
            columnFiltersDispatch,
            columnFilters,
            activeColumnFilterKeys,
          },
        },
        inputComponent: InputWithChips,
        startAdornment: (
          <InputAdornment position="start" className={styles.inputAdornment}>
            {searchIndexState === IndexState.Building &&
            (searchTerm !== "" ||
              Object.keys(columnFilters.filters).length > 0) ? (
              <Tooltip title="Building search index...">
                <CircularProgress size={20} />
              </Tooltip>
            ) : (
              <SearchIcon key="search" fontSize="small" />
            )}
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end" className={styles.inputAdornment}>
            <IconButton
              title="Enable regular expressions"
              color={searchMode === SearchMode.Regex ? "primary" : "default"}
              aria-pressed={searchMode === SearchMode.Regex}
              aria-label="Toggle search by regular expression"
              onClick={() => {
                const nextSearchMode =
                  searchMode === SearchMode.Regex
                    ? SearchMode.String
                    : SearchMode.Regex;
                setSearchMode(nextSearchMode);
              }}
              size="small"
            >
              <RegexIcon />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}
type InputWithChipsProps = InputBaseComponentProps & {
  hostProps: {
    activeColumnFilterKeys: string[];
    searchTerm: string;
    lastChipKey: string;
    columnFilters: ColumnFiltersState;
    columnFiltersDispatch: React.Dispatch<ColumnFiltersAction>;
  };
};
const InputWithChips = React.forwardRef<
  HTMLInputElement | null,
  InputWithChipsProps
>(function InputWithChips(props, ref) {
  const { hostProps, ...inputElementProps } = props;
  const {
    activeColumnFilterKeys,
    searchTerm,
    columnFiltersDispatch,
    lastChipKey,
    columnFilters,
  } = hostProps;
  return (
    <div className={styles.inputContainer}>
      {activeColumnFilterKeys.map((columnId) => {
        return (
          <ColumnFilterChip
            key={columnId}
            // Mark the last chip as selected when there is no search term.
            // this indicates that they can delete the chip with the delete key.
            highlight={() => lastChipKey === columnId && searchTerm === ""}
            columnId={columnId}
            columnFilters={columnFilters}
            columnFiltersDispatch={columnFiltersDispatch}
          />
        );
      })}
      <input {...inputElementProps} ref={ref} />
    </div>
  );
});

function ColumnFilterChip({
  highlight,
  columnId,
  columnFilters,
  columnFiltersDispatch,
}: {
  highlight?: () => boolean;
  columnId: string;
  columnFilters: ColumnFiltersState;
  columnFiltersDispatch: React.Dispatch<ColumnFiltersAction>;
}) {
  const popup = usePopupState();
  const column = columnFilters.columns[columnId];
  const filter = columnFilters.filters[columnId];
  return (
    <>
      <Chip
        className={styles.columnFilterChip}
        component="button"
        variant="outlined"
        label={`${column.name}: ${filter}`}
        key={columnId}
        sx={{
          backgroundColor: highlight?.() ? "action.hover" : undefined,
          transition: "background-color 0.3s ease",
        }}
        onDelete={() => {
          columnFiltersDispatch(new DeleteFilterAction({ columnId }));
        }}
        onClick={(event) => popup.open({ anchorEl: event.currentTarget })}
      />
      <ColumnFilterPopover
        popup={popup}
        filter={filter}
        changeFilter={(changedFilter) => {
          columnFiltersDispatch(
            new SetFilterAction({ columnId, filter: changedFilter }),
          );
        }}
      />
    </>
  );
}
